import { useAuth } from "@auth/AuthProvider";
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { Church, ChurchStaff, StaffAction, SupportedChurchVideoService } from '@interfaces/church.interfaces'
import IntegrationsController from '@data/controller/IntegrationsController'
import { ExternalVendor } from '@interfaces/external-vendor.interface'
import {
  IntegrationConnection,
  integrationsFromResponse,
  isIntegrationUsable
} from '@interfaces/integration.interface'

import NetworkController, { HTTPMethod, HTTPRequest } from '@data/controller/NetworkController'
import { useNavigate } from "react-router-dom";

export interface Campus {
  id: string;
  churchID: string;

  attributes: {
    name: string;
    address?: {
      street: string;
      state: string;
      zip: string;
      city: string;
      country: string;
    };
  };
}

export interface Category {
  id: string;
  churchID: string;
  name: string;
}

interface ChurchFetchResponse {
  church: Church;
  staff: ChurchStaff[];
  campuses: Campus[];
  categories: Category[];
}

interface ChurchDataProviderType {
  church?: Church;
  campuses: Campus[];
  categories: Category[];
  staff: ChurchStaff[];
  connectionStatus?: Record<ExternalVendor, boolean> | null;
  integrations?: IntegrationConnection[] | null;
  reloadChurch: () => void;
  reloadConnectionStatus: () => Promise<void>;
  clear: () => void
}

let ChurchDataContext = createContext<ChurchDataProviderType>(null!);

export default function ChurchDataProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const navigate = useNavigate();

  let [church, setChurch] = useState<Church | undefined>(undefined);
  let [campuses, setCampuses] = useState<Campus[]>([]);
  let [categories, setCategories] = useState<Category[]>([]);
  let [staff, setStaff] = useState<ChurchStaff[]>([]);
  let [integrations, setIntegrations] = useState<IntegrationConnection[] | null>(null);

  // Derived so the Events pages can keep asking "is Planning Center on?"
  // without knowing about connection records. Only an ACTIVE connection counts
  // as connected: one needing reauthorization can't make a request.
  const connectionStatus = useMemo(() => {
    // Captured in a const so the null check narrows inside the callback below;
    // `integrations` is a `let` from useState, which TypeScript won't narrow
    // across a closure.
    const currentIntegrations = integrations;

    if (!currentIntegrations) return null;

    return Object.values(ExternalVendor).reduce((statuses, vendor) => {
      statuses[vendor] = currentIntegrations.some(
        (connection) => connection.provider === vendor && isIntegrationUsable(connection)
      );

      return statuses;
    }, {} as Record<ExternalVendor, boolean>);
  }, [integrations]);

  const reloadChurch = useCallback(async (churchId: string) => {
    if (!auth.user) return;

    const response = await NetworkController.performRequestAt<ChurchFetchResponse>(
      process.env.REACT_APP_SANCTUARY_API_URL || "",
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchId}?includeStaff=true&includeConnectionMetadata=true`),
      auth.consumeRefreshToken
    );

    if (response.error || !response.body) {
      return auth.signOut(() => {
        navigate("/")
      });
    }

    const { church, staff, campuses, categories } = response.body;

    setChurch(church)
    setStaff(staff === undefined ? [] : staff)
    setCampuses(campuses === undefined ? [] : campuses)
    setCategories(categories === undefined ? [] : categories)

    // also refresh connection status
    try {
      const resp = await IntegrationsController.fetchIntegrations(churchId, auth);
      if (!resp.error && resp.body) {
        setIntegrations(integrationsFromResponse(resp.body));
      }
    } catch (err) {
      console.error('Failed to load connection status', err);
    }

    // reloadCurrentWeekCheckInData(churchId);
  }, [auth, navigate])

  const reloadConnectionStatus = useCallback(async () => {
    if (!auth.churchId) return;
    try {
      const resp = await IntegrationsController.fetchIntegrations(auth.churchId, auth);
      if (!resp.error && resp.body) {
        setIntegrations(integrationsFromResponse(resp.body));
      }
    } catch (err) {
      console.error('Failed to load connection status', err);
    }
  }, [auth]);

  const clear = () => {
    setChurch(undefined)
    setStaff([])
    setCampuses([])
    setCategories([])
    setIntegrations(null);
  }

  // Modify data when user changes
  useEffect(() => {
    if (auth.user !== undefined && auth.churchId !== undefined) {
      reloadChurch(auth.churchId)
    } else {
      clear();
    }
  }, [auth.user, reloadChurch, auth.churchId]);

  let value = {
    church, staff, campuses, categories, connectionStatus, integrations, reloadConnectionStatus, reloadChurch: () => {
      if (!auth.user || !auth.churchId) return;
      reloadChurch(auth.churchId);
    }, clear
  };

  return <ChurchDataContext.Provider value={value}>{children}</ChurchDataContext.Provider>;
}

export function useChurchDataProvider() {
  return React.useContext(ChurchDataContext);
}
